import React, { useState, useCallback, useEffect } from 'react';
import { Building2, Loader2, ChevronLeft, ChevronRight, Globe } from 'lucide-react';

import { AddressForm } from '../../../components/forms/AddressForm';
import { AddressData, validateAddress, getTimezoneFromLatLng, US_TIMEZONE_OPTIONS } from '../../../lib/utils/geocoding';
import { devLog } from '../../../utils/devLog';

interface LocationFormData {
  name: string;
}

interface LocationDetailsStepProps {
  initialData?: Partial<LocationFormData>;
  addressInitialData?: Partial<AddressData>;
  companyData?: {
    street: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
    latitude?: number;
    longitude?: number;
  };
  onNext: (data: { name: string; addressData: AddressData; timezone: string }) => void;
  onBack: () => void;
  disabled?: boolean;
  isNewLocation?: boolean;
}

export function LocationDetailsStep({
  initialData = {},
  addressInitialData = {},
  companyData,
  onNext,
  onBack,
  disabled = false,
  isNewLocation = true,
}: LocationDetailsStepProps) {
  const [formData, setFormData] = useState<LocationFormData>({
    name: initialData.name || '',
  });
  const [addressData, setAddressData] = useState<AddressData | null>(
    addressInitialData && Object.keys(addressInitialData).length > 0
      ? (addressInitialData as AddressData)
      : null
  );
  const [addressFormData, setAddressFormData] = useState<Partial<AddressData>>(
    addressInitialData || {}
  );
  const [addressError, setAddressError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [timezoneLoading, setTimezoneLoading] = useState(false);
  const [timezoneManual, setTimezoneManual] = useState(false);

  const handleAddressValidate = useCallback((isValid: boolean, data?: AddressData) => {
    if (!isValid) {
      setAddressError('Please provide a valid address');
      setAddressData(null);
    } else {
      setAddressError(null);
      if (data) {
        setAddressData(data);
        setAddressFormData(data);
      }
    }
  }, []);

  // Auto-resolve timezone when address data has coordinates
  useEffect(() => {
    if (addressData?.latitude && addressData?.longitude && !timezoneManual) {
      setTimezoneLoading(true);
      getTimezoneFromLatLng(addressData.latitude, addressData.longitude)
        .then((tz) => {
          setTimezone(tz);
          setTimezoneLoading(false);
        })
        .catch(() => {
          setTimezone(null);
          setTimezoneLoading(false);
        });
    }
  }, [addressData?.latitude, addressData?.longitude, timezoneManual]);

  const handleCopyFromCompany = useCallback(() => {
    if (companyData) {
      const companyAddressData: Partial<AddressData> = {
        street: companyData.street || '',
        unit: companyData.street2 || '',
        city: companyData.city || '',
        state: companyData.state || '',
        zip: companyData.zip || '',
        latitude: companyData.latitude,
        longitude: companyData.longitude,
        // Build formatted address for display
        formatted_address: [
          companyData.street,
          companyData.street2,
          companyData.city,
          companyData.state,
          companyData.zip,
        ].filter(Boolean).join(', '),
      };
      setAddressData(companyAddressData as AddressData);
      setAddressFormData(companyAddressData);
      setAddressError(null);
    }
  }, [companyData]);

  const handleNext = async () => {
    if (!formData.name.trim()) {
      setAddressError('Please enter a location name');
      return;
    }

    if (!addressData) {
      setAddressError('Please provide a valid address');
      return;
    }

    // Validate address has required fields
    const validation = validateAddress(addressData, ['street', 'city', 'state', 'zip']);
    if (!validation.isValid) {
      setAddressError(`Please complete the address. Missing: ${validation.missingFields.join(', ')}`);
      return;
    }

    // Timezone is required
    if (!timezone) {
      setAddressError('Please wait for timezone to resolve or select one manually');
      return;
    }

    // Check if this is a manually entered address (no coordinates)
    const isManualEntry = !addressData.latitude || !addressData.longitude;

    if (isManualEntry) {
      // For manual entries, we'll proceed but show a warning that verification is needed
      devLog('Manual address entry detected - will require verification before billing');
    }

    setIsValidating(true);
    try {
      onNext({ name: formData.name.trim(), addressData, timezone });
    } finally {
      setIsValidating(false);
    }
  };

  const canProceed = formData.name.trim() && addressData && !addressError &&
                     addressData.street && addressData.city && addressData.state && addressData.zip &&
                     !!timezone;

  return (
    <div className="bg-[#221F1F] rounded-xl p-6 border border-[#504A4A]">
      <div className="text-center mb-6">
        <div className="w-12 h-12 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-3">
          <Building2 size={24} className="text-rollops-accent" />
        </div>
        <h2 className="text-lg font-semibold text-[#EBFFEE]">
          {isNewLocation ? 'Add New Location' : 'Edit Location'}
        </h2>
        <p className="text-sm text-[#A9A3A3] mt-1">
          Enter the location details
        </p>
      </div>

      <div className="space-y-6">
        {/* Location Name */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-[#EBFFEE]">
            Location Name <span className="text-rollops-accent">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Main Gym, Downtown Location"
            disabled={disabled}
            maxLength={150}
            className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 disabled:opacity-50"
          />
        </div>

        {/* Address Form */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-[#EBFFEE]">
            Address <span className="text-rollops-accent">*</span>
          </label>
          <AddressForm
            key={JSON.stringify(addressFormData)} // Force re-render when addressFormData changes
            initialData={addressFormData}
            onValidate={handleAddressValidate}
            onCopyFromCompany={companyData ? handleCopyFromCompany : undefined}
            disabled={disabled}
            hideTitle
            placeholder="Enter address or business name..."
            requiredFields={['street', 'city', 'state', 'zip']}
          />
          {addressError && (
            <p className="text-sm text-red-400 mt-2">{addressError}</p>
          )}
        </div>

        {/* Timezone Display */}
        {addressData && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[#EBFFEE] flex items-center gap-2">
              <Globe size={16} className="text-rollops-accent" />
              Timezone <span className="text-rollops-accent">*</span>
            </label>
            {timezoneLoading ? (
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-[#504A4A] text-[#A9A3A3]">
                <Loader2 size={16} className="animate-spin" />
                Resolving timezone...
              </div>
            ) : timezone && !timezoneManual ? (
              <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-[#504A4A]">
                <span className="text-[#EBFFEE]">{timezone}</span>
                <button
                  type="button"
                  onClick={() => setTimezoneManual(true)}
                  className="text-sm text-rollops-accent hover:underline"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <select
                  value={timezone || ''}
                  onChange={(e) => {
                    setTimezone(e.target.value || null);
                  }}
                  className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                >
                  <option value="">Select timezone...</option>
                  {US_TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} — {tz.value}
                    </option>
                  ))}
                </select>
                {!timezoneManual && (
                  <p className="text-xs text-[#A9A3A3]">
                    Could not auto-detect timezone. Please select manually.
                  </p>
                )}
                {timezoneManual && (
                  <button
                    type="button"
                    onClick={() => {
                      setTimezoneManual(false);
                      if (addressData?.latitude && addressData?.longitude) {
                        setTimezoneLoading(true);
                        getTimezoneFromLatLng(addressData.latitude, addressData.longitude)
                          .then((tz) => { setTimezone(tz); setTimezoneLoading(false); })
                          .catch(() => { setTimezoneLoading(false); });
                      }
                    }}
                    className="text-sm text-rollops-accent hover:underline"
                  >
                    Auto-detect
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex gap-3 mt-6">
        <button
          onClick={onBack}
          disabled={disabled || isValidating}
          className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <ChevronLeft size={18} />
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!canProceed || disabled || isValidating}
          className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {isValidating ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Validating...
            </>
          ) : (
            <>
              Next: Sports
              <ChevronRight size={18} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
